import React, { useEffect, useRef, useState } from "react";
import {
  getPrimaryCoachProfile,
  getAdditionalClubFeePreview,
  previewAdditionalClub,
  registerAdditionalClub,
  clubApplicantShroffVoucherUrl,
} from "../../services/coachClubService";
import { listMemberGenders } from "../../services/memberService";
import { checkRegistrationPaymentStatus } from "../../services/authService";
import { openPaymentGateway } from "../../services/cyberGatewayService";

const PRIMARY = "#1d4ed8";
const NAVY = "#0f1c3f";
const LIGHT = "#e8f0fe";

const TITLE_OPTIONS = ["Mr", "Mrs", "Ms", "Miss", "Dr", "Rev."];
const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];

// Maps CoachClubController's validator field names (from a 422 `errors` map)
// back onto this form's fields — same shape as ClubRegistration.jsx's
// CLUB_FIELD_MAP/COACH_FIELD_MAP, since the backend's normalizedAdditionalClubInput
// accepts the identical camelCase keys.
const CLUB_FIELD_MAP = {
  club_name: "name",
  reg_no: "regNo",
  primary_phone_number: "primaryPhone",
  secondary_phone_number: "secondaryPhone",
  address: "address",
};
const COACH_FIELD_MAP = {
  title: "title",
  initials: "initials",
  name_denoted_by_initials: "nameWithInitials",
  lastname: "lastName",
  member_gender_id: "memberGenderId",
  email: "email",
  nic: "nationalId",
  primary_phone: "primaryPhone",
  secondary_phone: "secondaryPhone",
  date_of_birth: "dob",
  address: "address",
};

const emptyCoach = () => ({
  id: Date.now() + Math.random(),
  title: "",
  initials: "",
  nameWithInitials: "",
  lastName: "",
  memberGenderId: "",
  email: "",
  nationalId: "",
  primaryPhone: "",
  secondaryPhone: "",
  dob: "",
  address: "",
  photo: null,
  photoPreview: null,
  photoError: "",
});

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
  </div>
);

const Input = ({ error, ...props }) => (
  <input
    {...props}
    className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 text-gray-700 placeholder-gray-400 ${
      error ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
    }`}
  />
);

const selectClass = (error) =>
  `w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 text-gray-700 ${
    error ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
  }`;

// "Register Another Club" — a coach who already has an account can register
// an additional club without an OTP gate (they're already authenticated).
// Mirrors ClubRegistration.jsx's wizard→preview→submit shape against
// CoachClubController's parallel /coach/clubs/additional* routes, then a
// bespoke card/counter payment step (mirroring BookingPaymentModal /
// IdCardReprintPaymentModal, NOT the pre-login PaymentMethod.jsx — that
// component hard-navigates to /payment-success on success, which would yank
// an already-logged-in coach out of their dashboard).
//
// Props: `onClose()`, `onRegistered()` — called once payment is confirmed
// "Paid" so the caller can refresh its club list; this modal does not
// close itself or refetch anything on the caller's behalf beyond that.
// `zIndexClass` overrides the overlay's stacking (default z-50, same as every
// existing caller) — ClubSelectModal nests this inside its own z-[60] popup
// and needs it painted above that, not behind it.
export default function RegisterAdditionalClubModal({ onClose, onRegistered, zIndexClass = "z-50" }) {
  // phase: "form" -> "review" -> "submitted" -> "payment" -> "paid"
  const [phase, setPhase] = useState("form");

  const [primaryCoach, setPrimaryCoach] = useState(null);
  const [loadingPrimary, setLoadingPrimary] = useState(true);
  const [genders, setGenders] = useState([]);

  useEffect(() => {
    let active = true;
    setLoadingPrimary(true);
    Promise.all([getPrimaryCoachProfile().catch(() => null), listMemberGenders().catch(() => [])]).then(
      ([primary, genderList]) => {
        if (!active) return;
        setPrimaryCoach(primary?.primary_coach || null);
        setGenders(genderList);
        setLoadingPrimary(false);
      }
    );
    return () => {
      active = false;
    };
  }, []);

  const [club, setClub] = useState({ name: "", regNo: "", primaryPhone: "", secondaryPhone: "", address: "" });
  const [clubErrors, setClubErrors] = useState({});
  const [secondaryCoaches, setSecondaryCoaches] = useState([]);
  const [coachErrors, setCoachErrors] = useState({});
  const [navError, setNavError] = useState("");
  const coachPhotoRefs = useRef({});

  const coachesRef = useRef(secondaryCoaches);
  coachesRef.current = secondaryCoaches;
  useEffect(
    () => () => {
      coachesRef.current.forEach((c) => c.photoPreview && URL.revokeObjectURL(c.photoPreview));
    },
    []
  );

  // Live fee preview — base fee already covers the primary coach + 1
  // secondary; anything beyond that is billed per additional coach.
  const [feePreview, setFeePreview] = useState(null);
  useEffect(() => {
    let active = true;
    getAdditionalClubFeePreview(secondaryCoaches.length)
      .then((data) => active && setFeePreview(data))
      .catch(() => active && setFeePreview(null));
    return () => {
      active = false;
    };
  }, [secondaryCoaches.length]);

  const handleClubChange = (e) => {
    setClub((c) => ({ ...c, [e.target.name]: e.target.value }));
    setClubErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const clearCoachError = (id, field) =>
    setCoachErrors((prev) => {
      if (!prev[id]?.[field]) return prev;
      return { ...prev, [id]: { ...prev[id], [field]: "" } };
    });

  const handleCoachChange = (id, field, value) => {
    setSecondaryCoaches((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
    clearCoachError(id, field);
  };

  const handleCoachPhoto = (id, file) => {
    if (!file) return;
    const invalid = !ALLOWED_PHOTO_TYPES.includes(file.type)
      ? "Use a JPG, PNG, or WebP image"
      : file.size > 2 * 1024 * 1024
      ? "Max 2MB"
      : "";
    const preview = invalid ? null : URL.createObjectURL(file);
    setSecondaryCoaches((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        if (c.photoPreview) URL.revokeObjectURL(c.photoPreview);
        return invalid ? { ...c, photo: null, photoPreview: null, photoError: invalid } : { ...c, photo: file, photoPreview: preview, photoError: "" };
      })
    );
    if (!invalid) clearCoachError(id, "photo");
  };

  const addCoach = () => setSecondaryCoaches((prev) => [...prev, emptyCoach()]);
  const removeCoach = (id) => {
    const removed = secondaryCoaches.find((c) => c.id === id);
    if (removed?.photoPreview) URL.revokeObjectURL(removed.photoPreview);
    setSecondaryCoaches((prev) => prev.filter((c) => c.id !== id));
    setCoachErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const coachErr = (id, field) => coachErrors[id]?.[field] || "";

  const validateForm = () => {
    const cErrs = {};
    if (!club.name.trim()) cErrs.name = "Required";
    if (!club.regNo.trim()) cErrs.regNo = "Required";
    if (!club.primaryPhone.trim()) cErrs.primaryPhone = "Required";
    if (!club.address.trim()) cErrs.address = "Required";
    setClubErrors(cErrs);

    const allCoachErrs = {};
    secondaryCoaches.forEach((c) => {
      const e = {};
      if (!c.title) e.title = "Required";
      if (!c.initials.trim()) e.initials = "Required";
      if (!c.nameWithInitials.trim()) e.nameWithInitials = "Required";
      if (!c.lastName.trim()) e.lastName = "Required";
      if (!c.memberGenderId) e.memberGenderId = "Required";
      if (!c.email.trim() || !c.email.includes("@")) e.email = "Enter a valid email.";
      if (!c.primaryPhone.trim()) e.primaryPhone = "Required";
      if (!c.address.trim()) e.address = "Required";
      if (Object.keys(e).length) allCoachErrs[c.id] = e;
    });
    setCoachErrors(allCoachErrs);

    return Object.keys(cErrs).length === 0 && Object.keys(allCoachErrs).length === 0;
  };

  // Builds the multipart payload sent to both the preview and store routes,
  // matching CoachClubController::normalizedAdditionalClubInput's accepted
  // camelCase keys.
  const buildFormData = () => {
    const fd = new FormData();
    fd.append("clubName", club.name);
    fd.append("regNo", club.regNo);
    fd.append("primaryPhoneNumber", club.primaryPhone);
    if (club.secondaryPhone) fd.append("secondaryPhoneNumber", club.secondaryPhone);
    fd.append("address", club.address);

    const coachPayload = secondaryCoaches.map((c) => ({
      title: c.title,
      initials: c.initials,
      nameWithInitials: c.nameWithInitials,
      lastName: c.lastName,
      memberGenderId: c.memberGenderId,
      email: c.email,
      nic: c.nationalId || null,
      primaryPhone: c.primaryPhone,
      secondaryPhone: c.secondaryPhone || null,
      dob: c.dob || null,
      address: c.address,
    }));
    fd.append("secondary_coaches", JSON.stringify(coachPayload));
    secondaryCoaches.forEach((c, i) => {
      if (c.photo) fd.append(`secondary_coach_photos[${i}]`, c.photo);
    });
    return fd;
  };

  // Pushes a backend 422 `errors` map onto the form, same dot-path parsing as
  // ClubRegistration.jsx's applyBackendErrors.
  const applyBackendErrors = (errors, fallback) => {
    const clubErrs = {};
    const coachErrs = {};
    const general = [];
    Object.entries(errors || {}).forEach(([key, msgs]) => {
      const msg = Array.isArray(msgs) ? msgs[0] : String(msgs);
      let m;
      if ((m = key.match(/^secondary_coaches\.(\d+)\.(.+)$/))) {
        const coach = secondaryCoaches[Number(m[1])];
        const field = COACH_FIELD_MAP[m[2]];
        if (coach && field) coachErrs[coach.id] = { ...(coachErrs[coach.id] || {}), [field]: msg };
        else general.push(msg);
      } else if ((m = key.match(/^secondary_coach_photos\.(\d+)$/))) {
        const coach = secondaryCoaches[Number(m[1])];
        if (coach) coachErrs[coach.id] = { ...(coachErrs[coach.id] || {}), photo: msg };
        else general.push(msg);
      } else if (CLUB_FIELD_MAP[key]) {
        clubErrs[CLUB_FIELD_MAP[key]] = msg;
      } else {
        general.push(msg);
      }
    });
    if (Object.keys(clubErrs).length) setClubErrors((prev) => ({ ...prev, ...clubErrs }));
    if (Object.keys(coachErrs).length) {
      setCoachErrors((prev) => {
        const next = { ...prev };
        Object.entries(coachErrs).forEach(([id, fields]) => {
          next[id] = { ...(next[id] || {}), ...fields };
        });
        return next;
      });
    }
    setNavError(general[0] || fallback || "Please correct the highlighted fields.");
  };

  const [validating, setValidating] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [registeredClub, setRegisteredClub] = useState(null);

  const handleContinue = async () => {
    if (!validateForm()) {
      setNavError("Please correct the highlighted fields below.");
      return;
    }
    setNavError("");
    setValidating(true);
    try {
      const res = await previewAdditionalClub(buildFormData());
      setPreviewData(res?.data || null);
      setPhase("review");
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) applyBackendErrors(data.errors, data.message);
      else setNavError(data?.message || "We couldn't validate your details. Please try again.");
    } finally {
      setValidating(false);
    }
  };

  const handleConfirmRegister = async () => {
    setSubmitting(true);
    setNavError("");
    try {
      const data = await registerAdditionalClub(buildFormData());
      setRegisteredClub({
        process_id: data?.process_id,
        payable_amount: data?.payable_amount,
        payment_reference: data?.external_payment_reference || data?.payment_reference,
        payment_payload: data?.payment_payload,
        club_name: club.name,
      });
      setPhase("submitted");
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) {
        applyBackendErrors(data.errors, data.message);
        setPhase("form");
      } else {
        setNavError(data?.message || "Club registration failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Payment step state (mirrors BookingPaymentModal / IdCardReprintPaymentModal) ──
  const [selectedMethod, setSelectedMethod] = useState("card");
  const [gatewayOpened, setGatewayOpened] = useState(0);
  const [paymentError, setPaymentError] = useState("");
  const popupRef = useRef(null);
  const [verifying, setVerifying] = useState(false);
  const [paymentNotice, setPaymentNotice] = useState("");
  const [counterConfirmed, setCounterConfirmed] = useState(false);
  const [paidResult, setPaidResult] = useState(false);

  const amount = registeredClub?.payable_amount != null ? Number(registeredClub.payable_amount) : null;
  const amountLabel = amount != null ? `LKR ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—";
  const voucherUrl = registeredClub
    ? clubApplicantShroffVoucherUrl(registeredClub.process_id, registeredClub.payment_reference)
    : null;

  const verifyPayment = async () => {
    if (!registeredClub?.process_id || !registeredClub?.payment_reference) return;
    setVerifying(true);
    setPaymentNotice("");
    try {
      const res = await checkRegistrationPaymentStatus({
        process_type: "club",
        process_id: registeredClub.process_id,
        payment_reference: registeredClub.payment_reference,
      });
      if (res?.payment_status === "Paid") {
        setPaidResult(true);
        setPhase("paid");
        return;
      }
      setPaymentNotice(
        res?.payment_status
          ? `Payment status: ${res.payment_status}. If you completed the payment, please wait a moment and check again.`
          : "Payment is still pending. If you completed the payment, please wait a moment and check again."
      );
    } catch {
      setPaymentNotice("Could not verify payment status. Please check again.");
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    if (!gatewayOpened) return undefined;
    const popup = popupRef.current;
    if (!popup || !registeredClub?.process_id || !registeredClub?.payment_reference) return undefined;

    let stopped = false;
    let inFlight = false;
    const stopPolling = () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };
    const tick = async () => {
      if (stopped || inFlight) return;
      if (popup.closed) {
        stopPolling();
        verifyPayment();
        return;
      }
      inFlight = true;
      try {
        const res = await checkRegistrationPaymentStatus({
          process_type: "club",
          process_id: registeredClub.process_id,
          payment_reference: registeredClub.payment_reference,
        });
        if (stopped) return;
        if (res?.payment_status === "Paid") {
          stopPolling();
          popup.close();
          setPaidResult(true);
          setPhase("paid");
        }
      } catch {
        // Transient error — keep polling.
      } finally {
        inFlight = false;
      }
    };
    const timer = setInterval(tick, 3000);
    const onFocusOrVisible = () => {
      if (document.visibilityState === "hidden") return;
      tick();
    };
    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", onFocusOrVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayOpened]);

  const handlePaymentContinue = () => {
    setPaymentError("");
    setPaymentNotice("");
    if (selectedMethod === "counter") {
      setCounterConfirmed(true);
      return;
    }
    if (!registeredClub?.payment_payload) {
      setPaymentError("Missing payment details. Please refresh and try again.");
      return;
    }
    const popup = openPaymentGateway(registeredClub.payment_payload);
    if (!popup) {
      popupRef.current = null;
      setPaymentError("Your browser blocked the payment window. Please allow popups for this site and try again.");
      return;
    }
    popupRef.current = popup;
    setGatewayOpened((n) => n + 1);
  };

  const confirmingPayment = selectedMethod === "card" && gatewayOpened > 0 && !paymentNotice && !paidResult;

  const feeRows = (fee) =>
    fee
      ? [
          [`Base fee (Club + ${fee.included_coaches} Coaches)`, Number(fee.club_registration_fee)],
          fee.additional_coach_count > 0
            ? [`Additional coaches × ${fee.additional_coach_count} (LKR ${Number(fee.additional_coach_fee).toLocaleString()} each)`, Number(fee.additional_fee)]
            : null,
        ].filter(Boolean)
      : [];

  const totalFee = feePreview?.total_fee != null ? Number(feePreview.total_fee) : null;

  return (
    <div className={`fixed inset-0 ${zIndexClass} flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm`} role="dialog" aria-modal="true">
      <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
        {(phase === "form" || phase === "review") && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* ══ FORM ══ */}
        {phase === "form" && (
          <>
            <h2 className="text-lg font-bold pr-8" style={{ color: NAVY }}>Register Another Club</h2>
            <p className="text-sm text-gray-500 mb-5">Your own details are locked — only the club and any secondary coaches are editable.</p>

            {loadingPrimary ? (
              <div className="flex items-center justify-center py-10">
                <svg className="animate-spin w-7 h-7" fill="none" viewBox="0 0 24 24" stroke={PRIMARY}>
                  <circle className="opacity-25" cx="12" cy="12" r="10" strokeWidth="4" />
                  <path className="opacity-75" strokeWidth="4" strokeLinecap="round" d="M4 12a8 8 0 018-8" />
                </svg>
              </div>
            ) : (
              <>
                {primaryCoach && (
                  <div className="rounded-xl p-4 mb-5 flex items-center gap-3" style={{ backgroundColor: LIGHT }}>
                    <div className="w-11 h-11 rounded-full bg-white overflow-hidden flex-shrink-0 flex items-center justify-center border border-white">
                      {primaryCoach.photo_url ? (
                        <img src={primaryCoach.photo_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <svg className="w-5 h-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold truncate" style={{ color: NAVY }}>
                        {[primaryCoach.title, primaryCoach.initials, primaryCoach.lastname].filter(Boolean).join(" ")}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{primaryCoach.email} — Primary Coach (locked)</p>
                    </div>
                  </div>
                )}

                <p className="text-xs font-bold text-blue-700 mb-2">Club Details</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <Field label="Club Name" required error={clubErrors.name}>
                    <Input name="name" value={club.name} onChange={handleClubChange} placeholder="e.g. Rugby Club" error={clubErrors.name} />
                  </Field>
                  <Field label="Register Number" required error={clubErrors.regNo}>
                    <Input name="regNo" value={club.regNo} onChange={handleClubChange} placeholder="CLUB-002" error={clubErrors.regNo} />
                  </Field>
                  <Field label="Primary Phone" required error={clubErrors.primaryPhone}>
                    <Input name="primaryPhone" value={club.primaryPhone} onChange={handleClubChange} placeholder="077 123 4567" error={clubErrors.primaryPhone} />
                  </Field>
                  <Field label="Secondary Phone" error={clubErrors.secondaryPhone}>
                    <Input name="secondaryPhone" value={club.secondaryPhone} onChange={handleClubChange} placeholder="Optional" />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Address" required error={clubErrors.address}>
                      <Input name="address" value={club.address} onChange={handleClubChange} placeholder="123, Lake View Road, Nugegoda" error={clubErrors.address} />
                    </Field>
                  </div>
                </div>

                <div className="mb-2">
                  <p className="text-xs font-bold text-blue-700">Secondary Coaches (optional)</p>
                </div>

                {secondaryCoaches.length === 0 && (
                  <p className="text-xs text-gray-400 mb-4">No secondary coaches added — the base fee covers the club + primary coach.</p>
                )}

                <div className="space-y-4 mb-4">
                  {secondaryCoaches.map((coach, idx) => (
                    <div key={coach.id} className="rounded-xl border border-gray-100 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-bold text-gray-600">Secondary Coach {idx + 1}</p>
                        <button onClick={() => removeCoach(coach.id)} className="text-xs font-semibold text-red-500 hover:underline">
                          Remove
                        </button>
                      </div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-full bg-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
                          {coach.photoPreview ? (
                            <img src={coach.photoPreview} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <svg className="w-5 h-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          )}
                        </div>
                        <div>
                          <input
                            ref={(el) => (coachPhotoRefs.current[coach.id] = el)}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={(e) => handleCoachPhoto(coach.id, e.target.files?.[0])}
                          />
                          <button
                            onClick={() => coachPhotoRefs.current[coach.id]?.click()}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition"
                          >
                            {coach.photo ? "Change Photo" : "Upload Photo (optional)"}
                          </button>
                          {coach.photoError && <p className="text-xs text-red-500 mt-1">{coach.photoError}</p>}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Field label="Title" required error={coachErr(coach.id, "title")}>
                          <select value={coach.title} onChange={(e) => handleCoachChange(coach.id, "title", e.target.value)} className={selectClass(coachErr(coach.id, "title"))}>
                            <option value="">-- Select --</option>
                            {TITLE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </Field>
                        <Field label="Initials" required error={coachErr(coach.id, "initials")}>
                          <Input value={coach.initials} onChange={(e) => handleCoachChange(coach.id, "initials", e.target.value)} placeholder="A.B." error={coachErr(coach.id, "initials")} />
                        </Field>
                        <Field label="Name Denoted by Initials" required error={coachErr(coach.id, "nameWithInitials")}>
                          <Input value={coach.nameWithInitials} onChange={(e) => handleCoachChange(coach.id, "nameWithInitials", e.target.value)} placeholder="A.B. Perera" error={coachErr(coach.id, "nameWithInitials")} />
                        </Field>
                        <Field label="Last Name" required error={coachErr(coach.id, "lastName")}>
                          <Input value={coach.lastName} onChange={(e) => handleCoachChange(coach.id, "lastName", e.target.value)} placeholder="Perera" error={coachErr(coach.id, "lastName")} />
                        </Field>
                        <Field label="Gender" required error={coachErr(coach.id, "memberGenderId")}>
                          <select value={coach.memberGenderId} onChange={(e) => handleCoachChange(coach.id, "memberGenderId", e.target.value)} className={selectClass(coachErr(coach.id, "memberGenderId"))}>
                            <option value="">-- Select --</option>
                            {genders.map((g) => <option key={g.category_id} value={g.category_id}>{g.description}</option>)}
                          </select>
                        </Field>
                        <Field label="Email" required error={coachErr(coach.id, "email")}>
                          <Input type="email" value={coach.email} onChange={(e) => handleCoachChange(coach.id, "email", e.target.value)} placeholder="coach@example.com" error={coachErr(coach.id, "email")} />
                        </Field>
                        <Field label="National ID">
                          <Input value={coach.nationalId} onChange={(e) => handleCoachChange(coach.id, "nationalId", e.target.value)} placeholder="Optional" />
                        </Field>
                        <Field label="Primary Phone" required error={coachErr(coach.id, "primaryPhone")}>
                          <Input value={coach.primaryPhone} onChange={(e) => handleCoachChange(coach.id, "primaryPhone", e.target.value)} placeholder="077 123 4567" error={coachErr(coach.id, "primaryPhone")} />
                        </Field>
                        <Field label="Secondary Phone">
                          <Input value={coach.secondaryPhone} onChange={(e) => handleCoachChange(coach.id, "secondaryPhone", e.target.value)} placeholder="Optional" />
                        </Field>
                        <Field label="Date of Birth">
                          <input type="date" value={coach.dob} onChange={(e) => handleCoachChange(coach.id, "dob", e.target.value)} max={new Date().toLocaleDateString("en-CA")} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700" />
                        </Field>
                        <div className="sm:col-span-2">
                          <Field label="Address" required error={coachErr(coach.id, "address")}>
                            <Input value={coach.address} onChange={(e) => handleCoachChange(coach.id, "address", e.target.value)} placeholder="123, Lake View Road, Nugegoda" error={coachErr(coach.id, "address")} />
                          </Field>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="flex justify-end">
                    <button onClick={addCoach} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border-2 border-blue-700 text-blue-700 hover:bg-blue-50 transition">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Coach
                    </button>
                  </div>
                </div>

                {feePreview && (
                  <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: LIGHT }}>
                    {feeRows(feePreview).map(([label, value]) => (
                      <div key={label} className="flex justify-between text-xs text-gray-600 py-0.5">
                        <span>{label}</span>
                        <span>LKR {value.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm font-bold pt-1.5 mt-1.5 border-t border-white" style={{ color: NAVY }}>
                      <span>Total</span>
                      <span>{totalFee != null ? `LKR ${totalFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—"}</span>
                    </div>
                  </div>
                )}

                {navError && <p className="text-sm text-red-500 mb-3">{navError}</p>}

                <div className="flex items-center justify-between">
                  <button onClick={onClose} className="px-5 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition">
                    Cancel
                  </button>
                  <button
                    onClick={handleContinue}
                    disabled={validating}
                    className="px-6 py-2.5 bg-blue-700 hover:bg-blue-800 disabled:bg-blue-300 text-white rounded-lg text-sm font-semibold transition"
                  >
                    {validating ? "Validating…" : "Preview & Continue"}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* ══ REVIEW ══ */}
        {phase === "review" && previewData && (
          <>
            <h2 className="text-lg font-bold pr-8" style={{ color: NAVY }}>Review Your Application</h2>
            <p className="text-sm text-gray-500 mb-5">Confirm your details before submitting.</p>

            <div className="rounded-xl border border-gray-100 p-4 mb-4">
              <p className="text-xs font-bold text-blue-700 mb-2">Club Information</p>
              {[
                ["Club Name", previewData.club_name],
                ["Register Number", previewData.reg_no],
                ["Primary Phone", previewData.primary_phone_number],
                ["Secondary Phone", previewData.secondary_phone_number],
                ["Address", previewData.address],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between py-1 border-b border-gray-50 last:border-0">
                  <span className="text-xs text-gray-500">{k}</span>
                  <span className="text-xs font-medium text-gray-700 text-right max-w-[60%] truncate">{v || "—"}</span>
                </div>
              ))}
            </div>

            <div className="rounded-xl border border-gray-100 p-4 mb-4">
              <p className="text-xs font-bold text-blue-700 mb-2">Coaches ({previewData.coaches?.length || 0})</p>
              {(previewData.coaches || []).map((c, i) => {
                // Backend attaches the real DB profile (incl. photo_url) onto any
                // secondary coach whose email matched an existing coach account
                // (ExistingCoachResolver::coachPayloadFromMember) — prefer that over
                // the locally-picked file preview, which only applies to a brand-new
                // coach. The primary coach's own row already carries photo_url too
                // (lockedPrimaryCoachPayload), so this covers i === 0 for free.
                const photoPreview = c.photo_url || (i > 0 ? secondaryCoaches[i - 1]?.photoPreview : null);
                const genderName = genders.find((g) => String(g.category_id) === String(c.member_gender_id))?.description;
                return (
                  <div key={i} className="py-2 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-3 mb-1.5">
                      <div className="w-9 h-9 rounded-full bg-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
                        {photoPreview ? (
                          <img src={photoPreview} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <svg className="w-4 h-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-xs font-semibold text-gray-800">
                            {[c.title, c.initials, c.lastname].filter(Boolean).join(" ")}
                          </p>
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${c.coach_role === "Primary Coach" ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                            {c.coach_role}
                          </span>
                          {c.existing_member && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">Existing coach — profile attached</span>}
                        </div>
                        {c.name_denoted_by_initials && (
                          <p className="text-xs text-gray-500">{c.name_denoted_by_initials}</p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5 pl-12">
                      {[
                        ["Email", c.email],
                        ["Gender", genderName],
                        ["National ID", c.nic],
                        ["Primary Phone", c.primary_phone],
                        ["Secondary Phone", c.secondary_phone],
                        ["Date of Birth", c.date_of_birth],
                        ["Address", c.address],
                      ].filter(([, v]) => v).map(([k, v]) => (
                        <div key={k} className="flex gap-1.5">
                          <span className="text-[11px] text-gray-400 flex-shrink-0 w-24">{k}</span>
                          <span className="text-[11px] font-medium text-gray-600 break-words">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {previewData.fee && (
              <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: LIGHT }}>
                {feeRows(previewData.fee).map(([label, value]) => (
                  <div key={label} className="flex justify-between text-xs text-gray-600 py-0.5">
                    <span>{label}</span>
                    <span>LKR {value.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm font-bold pt-1.5 mt-1.5 border-t border-white" style={{ color: NAVY }}>
                  <span>Total</span>
                  <span>LKR {Number(previewData.fee.total_fee).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            )}

            {navError && <p className="text-sm text-red-500 mb-3">{navError}</p>}

            <div className="flex items-center justify-between">
              <button onClick={() => setPhase("form")} className="px-5 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition">
                Back to Edit
              </button>
              <button
                onClick={handleConfirmRegister}
                disabled={submitting}
                className="px-6 py-2.5 bg-blue-700 hover:bg-blue-800 disabled:bg-blue-300 text-white rounded-lg text-sm font-semibold transition"
              >
                {submitting ? "Submitting…" : "Confirm & Register"}
              </button>
            </div>
          </>
        )}

        {/* ══ SUBMITTED ══ */}
        {phase === "submitted" && (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-blue-50">
              <svg className="w-8 h-8 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
            <p className="text-sm text-gray-500 mb-5">Complete your payment to activate the club.</p>
            <button
              onClick={() => setPhase("payment")}
              className="w-full bg-blue-700 hover:bg-blue-800 text-white font-semibold py-3 rounded-lg text-sm mb-3 transition"
            >
              Proceed to Payment
            </button>
            <button onClick={onClose} className="w-full text-sm text-gray-500 hover:text-blue-600">
              I'll Pay Later
            </button>
          </div>
        )}

        {/* ══ PAYMENT ══ */}
        {phase === "payment" && !counterConfirmed && !verifying && (
          <div className="max-w-md mx-auto">
            <h2 className="text-lg font-bold mb-1" style={{ color: NAVY }}>Complete Payment</h2>
            <p className="text-sm text-gray-500 mb-5">Amount due: <span className="font-semibold text-gray-700">{amountLabel}</span></p>

            {registeredClub?.payment_payload && (
              <label className={`relative flex items-start gap-3 rounded-xl border-2 px-4 py-3.5 cursor-pointer transition ${selectedMethod === "card" ? "border-blue-600 bg-blue-50/60" : "border-gray-200 hover:border-gray-300"}`}>
                <input type="radio" name="additionalClubPaymentMethod" className="sr-only" checked={selectedMethod === "card"} onChange={() => setSelectedMethod("card")} />
                <span className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedMethod === "card" ? "border-blue-600" : "border-gray-300"}`}>
                  {selectedMethod === "card" && <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="text-sm font-bold text-gray-900">Card Payment</span>
                  <span className="block text-xs text-gray-500 mt-0.5">Pay securely using your debit or credit card</span>
                </span>
              </label>
            )}

            <label className={`relative flex items-start gap-3 rounded-xl border-2 px-4 py-3.5 cursor-pointer transition mt-3 ${selectedMethod === "counter" ? "border-blue-600 bg-blue-50/60" : "border-gray-200 hover:border-gray-300"}`}>
              <input type="radio" name="additionalClubPaymentMethod" className="sr-only" checked={selectedMethod === "counter"} onChange={() => setSelectedMethod("counter")} />
              <span className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedMethod === "counter" ? "border-blue-600" : "border-gray-300"}`}>
                {selectedMethod === "counter" && <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
              </span>
              <span className="flex-1 min-w-0">
                <span className="text-sm font-bold text-gray-900">Pay at Counter</span>
                <span className="block text-xs text-gray-500 mt-0.5">Pay at the club office or administration counter</span>
              </span>
            </label>

            {paymentError && <p className="text-sm text-red-500 text-center mt-4">{paymentError}</p>}
            {paymentNotice && <p className="text-sm text-amber-600 text-center mt-4">{paymentNotice}</p>}

            <div className="border-t border-gray-100 mt-5 pt-5">
              <button
                onClick={handlePaymentContinue}
                disabled={confirmingPayment}
                className="w-full text-white font-semibold py-2.5 rounded-xl text-sm transition disabled:opacity-70"
                style={{ backgroundColor: PRIMARY }}
              >
                {confirmingPayment ? "Confirming Payment…" : selectedMethod === "card" && gatewayOpened ? "Reopen Payment Window" : "Proceed to Pay"}
              </button>
              {confirmingPayment && <p className="text-xs font-medium text-amber-600 text-center mt-3">Confirming your payment... please don't close this.</p>}
              {paymentNotice && (
                <button onClick={verifyPayment} className="w-full text-sm font-medium border rounded-xl py-2 mt-3 transition" style={{ color: PRIMARY, borderColor: LIGHT }}>
                  Check Payment Status Again
                </button>
              )}
              <button onClick={onClose} className="w-full text-sm font-medium text-gray-500 hover:text-gray-700 border border-gray-200 hover:bg-gray-50 rounded-xl py-2.5 mt-3 transition">
                Close
              </button>
            </div>
          </div>
        )}

        {phase === "payment" && verifying && (
          <div className="flex flex-col items-center gap-4 text-center py-10">
            <svg className="animate-spin w-10 h-10" fill="none" viewBox="0 0 24 24" stroke={PRIMARY}>
              <circle className="opacity-25" cx="12" cy="12" r="10" strokeWidth="4" />
              <path className="opacity-75" strokeWidth="4" strokeLinecap="round" d="M4 12a8 8 0 018-8" />
            </svg>
            <p className="text-base font-semibold" style={{ color: NAVY }}>Verifying your payment…</p>
          </div>
        )}

        {phase === "payment" && counterConfirmed && (
          <div className="max-w-sm mx-auto text-center py-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-3">
              <svg className="w-7 h-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3m6-14h.01M9 11h.01M9 15h.01M14 7h.01M14 11h.01M14 15h.01" />
              </svg>
            </div>
            <h2 className="text-lg font-bold" style={{ color: NAVY }}>Pay at Counter</h2>
            <p className="text-sm text-gray-500 mt-1">Take this reference to the club office / administration counter.</p>
            <div className="rounded-xl px-4 py-4 mt-4" style={{ backgroundColor: LIGHT }}>
              <p className="text-xs text-gray-500">Reference Number</p>
              <p className="text-xl font-extrabold mt-1 font-mono break-all" style={{ color: PRIMARY }}>{registeredClub?.payment_reference || "—"}</p>
              <p className="text-xs text-gray-500 mt-2">Amount due: <span className="font-semibold text-gray-700">{amountLabel}</span></p>
            </div>
            {voucherUrl && (
              <a href={voucherUrl} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 border-2 font-semibold py-2.5 rounded-xl text-sm mt-4 transition" style={{ borderColor: PRIMARY, color: PRIMARY }}>
                Download Voucher
              </a>
            )}
            <button onClick={() => setCounterConfirmed(false)} className="w-full text-sm font-medium text-gray-500 hover:text-gray-700 border border-gray-200 hover:bg-gray-50 rounded-xl py-2.5 mt-3 transition">
              Back
            </button>
            <button onClick={onClose} className="w-full px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition mt-3" style={{ backgroundColor: PRIMARY }}>
              Done — I'll Pay Later
            </button>
          </div>
        )}

        {/* ══ PAID ══ */}
        {phase === "paid" && (
          <div className="max-w-sm mx-auto text-center py-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-bold" style={{ color: NAVY }}>Payment Confirmed</h2>
            <p className="text-sm text-gray-500 mt-1">Your new club is now active.</p>
            <button
              onClick={() => {
                onRegistered?.();
                onClose();
              }}
              className="w-full mt-6 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition"
              style={{ backgroundColor: PRIMARY }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
